import React, { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Box, Button, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import { ActionsButton, Datatable } from 'ui';
import { useSelectedTableRows, useTable } from 'shared/hooks';
import { theme } from 'theme';
import { portfolioUploadQuery } from 'shared/constants/portfolioUploadQuery';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { LinkButton } from 'ui/Buttons';
import { MUIDataTableMeta } from 'mui-datatables';
import { SelectedEntities } from 'shared/types/portfolios';
import { useFeatures } from 'shared/hooks/useFeatures/useFeatures';
import { PortfolioUploadModal } from '../Modals/PortfolioUpload/PortfolioUploadModal';
import {
  useFetchPortfolios,
  useDeletePortfolios,
  useUpdatePortfolio,
} from '../GraphQL/portfolios/hooks';
import { CreateNewPortfolioModal } from '../Modals/CreateNewPortfolioModal';
import { multiline, makePortfolioTableData } from './utils';

const useStyles = makeStyles(() => ({
  root: {
    '& thead > tr': {
      whiteSpace: 'pre',
    },
  },
  nameButton: {
    minWidth: 'unset',
  },
  disabled: {
    opacity: 0.3,
  },
}));

type SelectedPortfolio = {
  id: string;
  name: string;
  description: string;
};

export const PortfolioManager: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [portfolioForUpdate, setPortfolioForUpdate] = useState<SelectedPortfolio>();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [portfolioUploadModalOpen, setPortfolioUploadModalOpen] = useState(false);
  const [createPortfolioModalOpen, setCreatePortfolioModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: '' });
  const { success } = useSnackbar();
  const updatePortfolio = useUpdatePortfolio();
  const deletePortfolios = useDeletePortfolios();
  const {
    singleSelect,
    multiSelect,
    currentSelect,
    selectedAll,
    onSelectAll,
    onSelectedChange,
    selectSingle,
    clearSingle,
  } = useSelectedTableRows();

  const features = useFeatures();

  const actionRender = (
    _: unknown,
    { rowData: [[id, artifactId]], rowIndex }: MUIDataTableMeta,
  ) => (
    <ActionsButton
      onOpen={() => selectSingle(id, artifactId)}
      onClose={() => clearSingle()}
      actions={getActions(singleSelect)}
      disabled={dedicatedIndexes.includes(rowIndex)}
    />
  );

  /* ******************************************************************** */
  /*                       Table columns definition                       */
  /* ******************************************************************** */

  const initialColumns = useCallback(
    () => [
      {
        name: 'name',
        label: t('PORTFOLIO.PORTFOLIO'),
        options: {
          customBodyRender: ([id, name, description]: string[]) => (
            <LinkButton
              to={id}
              label={name}
              className={classes.nameButton}
              tooltip={description}
              withPathname
            />
          ),
        },
      },
      {
        name: 'created_date',
        label: t('PORTFOLIO.UPLOAD_DATE'),
      },
      {
        name: 'noOfItems',
        label: t('PORTFOLIO.ENTITIES'),
        options: {
          sort: false,
        },
      },
      {
        name: 'instruments',
        label: t('PORTFOLIO.INSTRUMENTS'),
        options: {
          sort: false,
        },
      },
      {
        name: 'fundsAnalysis',
        label: t('PORTFOLIO.FUNDS_ANALYSIS'),
        options: {
          sort: false,
          display: features.fundAnalysisPortfolioManager,
        },
      },
      {
        name: 'esgImpactReviewCoverage',
        label: multiline(t('PORTFOLIO.ESG_IMPACT_REVIEW'), t('PORTFOLIO.COVERAGE')),
        options: {
          sort: false,
          display: false,
        },
      },
      {
        name: 'creditRatingCoverage',
        label: multiline(t('PORTFOLIO.CREDIT_RATING'), t('PORTFOLIO.COVERAGE')),
        options: {
          sort: false,
          display: false,
        },
      },
      {
        name: 'creditReviewCoverage',
        label: multiline(t('PORTFOLIO.CREDIT_REVIEW'), t('PORTFOLIO.COVERAGE')),
        options: {
          sort: false,
          display: false,
        },
      },
      {
        name: 'notifications',
        label: t('PORTFOLIO.NOTIFICATIONS'),
        options: {
          sort: false,
          customBodyRender: ([id, watchlistEnabled, hidden]: any[]) => {
            if (!hidden) {
              return (
                <IconButton
                  color={watchlistEnabled ? 'primary' : 'inherit'}
                  className={clsx(!watchlistEnabled && classes.disabled)}
                  onClick={toggleVisibility(id, watchlistEnabled)}
                  size="large"
                >
                  <VisibilityIcon />
                </IconButton>
              );
            }
            return null;
          },
        },
      },
      {
        name: 'activity',
        label: t('PORTFOLIO.ACTIVITY'),
        options: {
          customBodyRender: (item: unknown) =>
            item && (
              <Button variant="outlined" size="small" color="primary">
                {t('View activity')}
              </Button>
            ),
          display: features.activityInPortfolioManager,
        },
      },
      {
        name: 'moreActions',
        label: ' ',
        options: {
          sort: false,
          customBodyRender: actionRender,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, classes, singleSelect, multiSelect, currentSelect],
  );

  /* ******************************************************************** */
  /*                              Table data                              */
  /* ******************************************************************** */

  const { columns, pagination, onSortChange, onTableChange } = useTable({
    initialColumns,
  } as any);

  const { data, loading, refetch } = useFetchPortfolios(
    pagination.page * pagination.limit,
    pagination.limit,
    pagination.sort && [
      {
        column: pagination.sort.sortColumn,
        direction: pagination.sort.direction,
      },
    ],
  );
  const { total, portfolios } = data || { total: 0, portfolios: [] };

  const dedicatedIndexes =
    portfolios && portfolios.length
      ? portfolios
          .map((portfolio, i) => (portfolio.isDedicated ? i : -1))
          .filter((index) => index !== -1)
      : [];

  const isDedicatedPortfolioSelected = !!(
    portfolios &&
    portfolios.length &&
    currentSelect.count > 0 &&
    portfolios.find(
      ({ id: portfolioId, isDedicated }) => currentSelect.itemIds[portfolioId] && isDedicated,
    )
  );

  /* ******************************************************************** */
  /*                            Event handlers                            */
  /* ******************************************************************** */

  const openUploadPortfolioModal = () => navigate(`${pathname}?${portfolioUploadQuery.MODAL}`);

  const closeUploadPortfolioModal = () => navigate(`${pathname}`);

  const openCreatePortfolioModal = () => {
    if (portfolioForUpdate) {
      setPortfolioForUpdate(undefined);
    }
    setCreatePortfolioModalOpen(true);
  };

  const openRenamePortfolioModal = () => setCreatePortfolioModalOpen(true);

  const closeCreatePortfolioModal = () => setCreatePortfolioModalOpen(false);

  const toggleVisibility = (portfolioId: string, watchlistEnabled: boolean) => async () => {
    setUpdating(true);
    await updatePortfolio({ portfolioId, watchlistEnabled: !watchlistEnabled }).then(() => {
      success(
        watchlistEnabled
          ? t('PORTFOLIO.NOTIFICATIONS_DISABLED')
          : t('PORTFOLIO.NOTIFICATIONS_ENABLED'),
      );
    });
    await refetch();
    setUpdating(false);
  };

  const deletePortfolio = async () => {
    setUpdating(true);
    await deletePortfolios({ portfolioIds: Object.keys(currentSelect.itemIds) }).then(() => {
      success(t('PORTFOLIO.PORTFOLIO_DELETED'));
    });
    await refetch();
    setShouldRefresh({ value: true, reason: refreshReasons.DELETED });
    setUpdating(false);
  };

  const loadTableData = async () => {
    setUpdating(true);
    await refetch();
    setShouldRefresh({ value: true, reason: refreshReasons.UPDATED });
    setUpdating(false);
  };

  useEffect(() => {
    if (currentSelect.count === 1) {
      const selectedId = Object.keys(currentSelect.itemIds)[0];
      const selectedItem = portfolios.find((item) => item.id === selectedId);

      if (selectedItem) {
        setPortfolioForUpdate({
          id: selectedItem.id,
          name: selectedItem.name,
          description: selectedItem.description,
        });
      }
    }
  }, [currentSelect, portfolios]);

  /* ******************************************************************** */
  /*                             More actions                             */
  /* ******************************************************************** */

  const getActions = (items: SelectedEntities) => [
    {
      text: t('PORTFOLIO.UPLOAD_PORTFOLIO'),
      handler: openUploadPortfolioModal,
      hidden: items.count > 0,
    },
    {
      text: t('PORTFOLIO.CREATE_PORTFOLIO'),
      handler: openCreatePortfolioModal,
      hidden: items.count > 0,
    },
    {
      text: t('PORTFOLIO.RENAME_PORTFOLIO'),
      handler: openRenamePortfolioModal,
      hidden: items.count !== 1,
      disabled: isDedicatedPortfolioSelected,
    },
    {
      text: t('PORTFOLIO.DELETE_PORTFOLIO'),
      handler: deletePortfolio,
      hidden: items.count === 0,
      disabled: isDedicatedPortfolioSelected,
      color: theme.palette.error.main,
      confirmation: {
        title: 'Deleting portfolio',
        text: 'Do you really want to delete that portfolio?',
      },
    },
  ];

  /* ******************************************************************** */
  /*                             Side effects                             */
  /* ******************************************************************** */

  useEffect(() => {
    setPortfolioUploadModalOpen(search.includes(portfolioUploadQuery.MODAL));
  }, [search]);

  useEffect(() => {
    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ******************************************************************** */
  /*                                Render                                */
  /* ******************************************************************** */
  return (
    <>
      <Box className={classes.root}>
        <Datatable
          loading={loading || updating}
          columns={columns}
          pagination={pagination}
          data={makePortfolioTableData(portfolios)}
          notSelectableRows={dedicatedIndexes}
          total={total}
          onSelectedChange={onSelectedChange}
          toolbarData={getActions(multiSelect)}
          onTableChange={onTableChange}
          onSortChange={onSortChange}
          selectedAll={selectedAll}
          onSelectAllClick={onSelectAll}
          shouldRefresh={shouldRefresh}
          selectableRowsHeader={false}
        />
      </Box>
      <PortfolioUploadModal
        open={portfolioUploadModalOpen}
        onClose={closeUploadPortfolioModal}
        onUploaded={loadTableData}
      />
      <CreateNewPortfolioModal
        open={createPortfolioModalOpen}
        onClose={closeCreatePortfolioModal}
        onCreate={loadTableData}
        portfolio={portfolioForUpdate}
      />
    </>
  );
};
